import { D as generateRandomUUID, E as HANDOFF_RECEIVED_TOOL_NAME } from './utils-B62kN-vT.mjs';

const HANDOFF_DATA_MESSAGE_ID_PREFIX = "handoff-data-";
const INITIAL_MESSAGE_ID_PREFIX = "initial-message-";
const HANDOFF_RECEIVED_MESSAGE_ID_PREFIX = "handoff-received-";
const TOOL_CANCELLED_MESSAGE = "Tool cancelled by user";
const TOOL_CANCELLED_RESULT = {
  cancelled: true,
  message: TOOL_CANCELLED_MESSAGE,
  success: false,
  status: "cancelled"
};
const ADDITIONAL_HANDOFF_DATA_STRING = "<additionalHandoffData>";
const ADDITIONAL_HANDOFF_DATA_STRING_END = "</additionalHandoffData>";
const createHandoffDataMessages = (handoffPayload) => {
  const { fromCopilotId, toCopilotId, handoffData } = handoffPayload;
  const content = `
  Handoff data provided by ${fromCopilotId} copilot:
  ${ADDITIONAL_HANDOFF_DATA_STRING}
  ${JSON.stringify(handoffData)}
  ${ADDITIONAL_HANDOFF_DATA_STRING_END}

  Conversation has been handed over to ${toCopilotId} copilot from ${fromCopilotId} copilot.
  Conversation will now continue in ${toCopilotId} copilot.
  `;
  return [
    {
      id: `${HANDOFF_DATA_MESSAGE_ID_PREFIX}${generateRandomUUID()}-${fromCopilotId}`,
      role: "assistant",
      content,
      parts: [{ type: "text", text: content }]
    }
  ];
};
const createHandoffReceivedMessage = (handoffPayload) => {
  const toolCallId = `toolu_${generateRandomUUID()}`;
  return {
    role: "assistant",
    content: "",
    id: `${HANDOFF_RECEIVED_MESSAGE_ID_PREFIX}${generateRandomUUID()}`,
    parts: [
      {
        type: "step-start"
      },
      {
        type: "tool-invocation",
        toolInvocation: {
          state: "call",
          step: 0,
          toolCallId,
          toolName: HANDOFF_RECEIVED_TOOL_NAME,
          args: handoffPayload
        }
      }
    ],
    toolInvocations: [
      {
        state: "call",
        step: 0,
        toolCallId,
        toolName: HANDOFF_RECEIVED_TOOL_NAME,
        args: handoffPayload
      }
    ]
  };
};
const isHandoffDataMessage = (message) => {
  return message.id.startsWith(HANDOFF_DATA_MESSAGE_ID_PREFIX);
};
const isInitialMessage = (message) => {
  return message.id.startsWith(INITIAL_MESSAGE_ID_PREFIX);
};
const isHandoffReceivedMessage = (message) => {
  return message.id.startsWith(HANDOFF_RECEIVED_MESSAGE_ID_PREFIX);
};
const isStaleHandoffReceivedMessage = (message, currentCopilotId) => {
  const toolInvocationPart = message?.parts?.find(
    (part) => part.type === "tool-invocation" && part.toolInvocation?.toolName === HANDOFF_RECEIVED_TOOL_NAME
  );
  const receivedHandoffPayload = toolInvocationPart?.toolInvocation?.args;
  return isHandoffReceivedMessage(message) && receivedHandoffPayload && receivedHandoffPayload.toCopilotId !== currentCopilotId;
};
function idempotentMapToStructuredInitialMessages(initialMessages) {
  return initialMessages.map((m) => {
    if (isInitialMessage(m)) {
      return m;
    } else {
      return {
        ...m,
        id: `${INITIAL_MESSAGE_ID_PREFIX}${m.id}`
      };
    }
  });
}
function findImmediateHandoffAndInitialMessages(messages) {
  const emptyResult = { initialMessages: [], handoffMessages: [] };
  if (messages.length === 0) {
    return emptyResult;
  }
  const lastMessage = messages[messages.length - 1];
  if (lastMessage.role !== "user") {
    return emptyResult;
  }
  if (messages.length === 1) {
    return emptyResult;
  }
  const takeConsecutiveFromEnd = (arr, predicate, fromIndex = arr.length - 1) => {
    const items = [];
    let currentIndex = fromIndex;
    while (currentIndex >= 0 && predicate(arr[currentIndex])) {
      items.unshift(arr[currentIndex]);
      currentIndex--;
    }
    return { items, lastIndex: currentIndex };
  };
  const messagesBeforeUser = messages.slice(0, -1);
  const { items: initialMessages, lastIndex: lastNonInitialIndex } = takeConsecutiveFromEnd(messagesBeforeUser, isInitialMessage);
  const handoffSearchStartIndex = initialMessages.length > 0 ? lastNonInitialIndex : messagesBeforeUser.length - 1;
  const { items: handoffMessages } = takeConsecutiveFromEnd(
    messagesBeforeUser,
    isHandoffDataMessage,
    handoffSearchStartIndex
  );
  return { initialMessages, handoffMessages };
}

const COPILOT_ID_TO_SPLIT_NAME = {
  editor: "ai-toolkit_copilot-editor",
  tables: "ai-toolkit_copilot-tables",
  interfaces: "ai-toolkit_copilot-interfaces",
  canvas: "ai-toolkit_copilot-canvas",
  memberhome: "ai-toolkit_copilot-memberhome",
  agents: "ai-toolkit_copilot-agents"
};
const FEATURE_TO_SPLIT_NAME = {
  handoffs: "ai-toolkit_copilot-handoffs"
};
const SPLIT_CLIENT_TIMEOUT_SECONDS = 10;
const SPLIT_CLIENT_RETRY_COUNT = 2;

function getSignupTimestamp(signup) {
  const timestamp = new Date(signup).getTime();
  if (isNaN(timestamp)) {
    throw new Error(`Invalid signup date format: ${signup}`);
  }
  return timestamp;
}
function getSplitTreatmentAttributes(profile) {
  const { signup, ...otherProps } = profile;
  const attributes = {
    ...otherProps,
    // Convert signup date to timestamp for Split compatibility
    signup: getSignupTimestamp(signup)
  };
  return attributes;
}

var CopilotMode = /* @__PURE__ */ ((CopilotMode2) => {
  CopilotMode2["autoBuild"] = "auto_build";
  CopilotMode2["askAsYouBuild"] = "ask_as_you_build";
  return CopilotMode2;
})(CopilotMode || {});

var FeedbackValue = /* @__PURE__ */ ((FeedbackValue2) => {
  FeedbackValue2["GOOD"] = "Good";
  FeedbackValue2["BAD"] = "Bad";
  return FeedbackValue2;
})(FeedbackValue || {});
var FeedbackType = /* @__PURE__ */ ((FeedbackType2) => {
  FeedbackType2["THUMBS_UP"] = "thumbsUp";
  FeedbackType2["THUMBS_DOWN"] = "thumbsDown";
  return FeedbackType2;
})(FeedbackType || {});
var FeedbackSentiment = /* @__PURE__ */ ((FeedbackSentiment2) => {
  FeedbackSentiment2["POSITIVE"] = "positive";
  FeedbackSentiment2["NEGATIVE"] = "negative";
  return FeedbackSentiment2;
})(FeedbackSentiment || {});

export { COPILOT_ID_TO_SPLIT_NAME as C, FeedbackValue as F, SPLIT_CLIENT_RETRY_COUNT as S, TOOL_CANCELLED_RESULT as T, SPLIT_CLIENT_TIMEOUT_SECONDS as a, CopilotMode as b, createHandoffDataMessages as c, createHandoffReceivedMessage as d, FEATURE_TO_SPLIT_NAME as e, findImmediateHandoffAndInitialMessages as f, getSplitTreatmentAttributes as g, idempotentMapToStructuredInitialMessages as h, isHandoffReceivedMessage as i, FeedbackType as j, FeedbackSentiment as k, isHandoffDataMessage as l, isStaleHandoffReceivedMessage as m };
//# sourceMappingURL=types-D6bvfc7F.mjs.map
